import { useState, useEffect } from 'react';
import { Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, Stack, FormControl, FormLabel, Input, Button, Switch, Typography, Box, Chip, Select, Option, Divider, IconButton, Sheet, Alert, ModalOverflow } from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Trash } from "@phosphor-icons/react";
import RichTextEditor from '../ui/RichTextEditor';
import { APIUploadQuestionImage, API_URL } from '../../api';



export default function QuestionModal({ open, category, question, onClose, onSave, token }) {
    const [formData, setFormData] = useState({
        text: '',
        description: '',
        type: 'single',
        explanation: '',
        skip_count_in_stats: false,
        is_active: true,
        category_ids: [],
        answers: []
    });
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [categories, setCategories] = useState([]);

    // Загружаем категории при открытии модального окна
    useEffect(() => {
        if (open && token) {
            fetchCategories();
        }
    }, [open, token]);

    // Устанавливаем категорию если передана
    useEffect(() => {
        if (open) {
            if (question) {
                // Режим редактирования - загружаем данные вопроса
                setFormData({
                    text: question.text || '',
                    description: question.description || '',
                    type: question.type || 'single',
                    explanation: question.explanation || '',
                    skip_count_in_stats: question.skip_count_in_stats || false,
                    is_active: question.is_active !== undefined ? question.is_active : true,
                    category_ids: question.categories ? question.categories.map(cat => cat.id) : [],
                    answers: question.answers ? question.answers.map(answer => ({
                        id: answer.id,
                        text: answer.text || '',
                        description: answer.description || '',
                        is_correct: answer.is_correct || false,
                        is_active: answer.is_active !== undefined ? answer.is_active : true
                    })) : []
                });
            } else {
                // Режим создания
                setFormData({
                    text: '',
                    description: '',
                    type: 'single',
                    explanation: '',
                    skip_count_in_stats: false,
                    is_active: true,
                    category_ids: category ? [category.id] : [],
                    answers: []
                });
            }
        }
    }, [open, question, category]);

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/content/flat/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();
            if (data.success) {
                setCategories(data.categories);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleImageUpload = async (file) => {
        setUploadingImage(true);
        try {
            const response = await APIUploadQuestionImage(token, file);
            if (response.success) {
                return response.url;
            } else {
                throw new Error(response.error);
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Ошибка при загрузке изображения');
            return null;
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await onSave(formData);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddAnswer = () => {
        setFormData(prev => ({
            ...prev,
            answers: [
                ...prev.answers,
                { text: '', description: '', is_correct: false, is_active: true }
            ]
        }));
    };

    const handleAnswerChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.map((answer, i) => 
                i === index ? { ...answer, [field]: value } : answer
            )
        }));
    };

    const handleDeleteAnswer = (index) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.filter((_, i) => i !== index)
        }));
    };

    const handleCorrectAnswerChange = (index, isCorrect) => {
        // Для single типа сбрасываем другие правильные ответы
        if (formData.type === 'single' && isCorrect) {
            setFormData(prev => ({
                ...prev,
                answers: prev.answers.map((answer, i) => ({
                    ...answer,
                    is_correct: i === index
                }))
            }));
        } else {
            handleAnswerChange(index, 'is_correct', isCorrect);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <Modal 
                    open={open} 
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10012
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            component={motion.div}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            sx={{
                                width: '90%',
                                maxWidth: 800,
                                // maxHeight: '90vh',
                                overflow: 'auto'
                            }}
                        >
                            <ModalClose />
                            
                            <DialogTitle>
                                {question ? 'Редактирование вопроса' : 'Создание вопроса'}
                                {category && !question && (
                                    <Typography level="body2" color="neutral">
                                        в категории "{category.name}"
                                    </Typography>
                                )}
                            </DialogTitle>

                            <DialogContent>
                                <Stack spacing={3} component="form" onSubmit={handleSubmit}>
                                    {uploadingImage && (
                                        <Alert color="info" variant="soft">
                                            Загрузка изображения...
                                        </Alert>
                                    )}

                                    {/* Категории */}
                                    <FormControl required>
                                        <FormLabel>Категории вопроса</FormLabel>
                                        <Select
                                            multiple
                                            slotProps={{ listbox: { sx: { zIndex: 10013 } } }}
                                            value={formData.category_ids}
                                            onChange={(e, value) => handleChange('category_ids', value)}
                                            disabled={!!category} // Блокируем если категория задана
                                        >
                                            {categories.map((cat) => (
                                                <Option key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                    {cat.parent_name && ` (${cat.parent_name})`}
                                                </Option>
                                            ))}
                                        </Select>
                                        {category && (
                                            <Typography level="body2" color="neutral" sx={{ mt: 1 }}>
                                                Категория задана автоматически
                                            </Typography>
                                        )}
                                    </FormControl>
                                    
                                    <Divider/>
                                    {/* Тип вопроса */}
                                    <FormControl required>
                                        <FormLabel>Тип вопроса</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10013 } } }}
                                            value={formData.type}
                                            onChange={(e, value) => handleChange('type', value)}
                                        >
                                            <Option value="single">Выбор одного варианта</Option>
                                            <Option value="multiple">Выбор нескольких вариантов</Option>
                                            <Option value="text">Текстовый ответ</Option>
                                        </Select>
                                    </FormControl>

                                    {/* Текст вопроса */}
                                    <FormControl required>
                                        <FormLabel>Текст вопроса</FormLabel>
                                        <Input
                                            value={formData.text}
                                            onChange={(e) => handleChange('text', e.target.value)}
                                            placeholder="Введите текст вопроса"
                                            disabled={loading}
                                        />
                                    </FormControl>

                                    {/* Описание вопроса */}
                                    <FormControl>
                                        <FormLabel>Описание вопроса</FormLabel>
                                        <RichTextEditor
                                            value={formData.description}
                                            onChange={(value) => handleChange('description', value)}
                                            onImageUpload={handleImageUpload}
                                            placeholder="Введите описание вопроса (можно использовать форматирование и изображения)"
                                        />
                                    </FormControl>

                                    {/* Варианты ответов (только для single/multiple) */}
                                    {(formData.type === 'single' || formData.type === 'multiple') && (
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <FormLabel>Варианты ответов</FormLabel>
                                                <Button
                                                    size="sm"
                                                    startDecorator={<Plus size={18} />}
                                                    onClick={handleAddAnswer}
                                                >
                                                    Добавить ответ
                                                </Button>
                                            </Box>

                                            <Stack spacing={2}>
                                                {formData.answers.map((answer, index) => (
                                                    <AnswerItem
                                                        key={index}
                                                        answer={answer}
                                                        index={index}
                                                        questionType={formData.type}
                                                        onChange={handleAnswerChange}
                                                        onCorrectChange={handleCorrectAnswerChange}
                                                        onDelete={handleDeleteAnswer}
                                                        onImageUpload={handleImageUpload}
                                                    />
                                                ))}

                                                {formData.answers.length === 0 && (
                                                    <Sheet variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 'md' }}>
                                                        <Typography level="body2" color="neutral">
                                                            Добавьте варианты ответов
                                                        </Typography>
                                                    </Sheet>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}

                                    <Divider />

                                    {/* Объяснение */}
                                    <FormControl>
                                        <FormLabel>Объяснение (показывается после ответа)</FormLabel>
                                        <RichTextEditor
                                            value={formData.explanation}
                                            onChange={(value) => handleChange('explanation', value)}
                                            onImageUpload={handleImageUpload}
                                            placeholder="Введите объяснение ответа"
                                        />
                                    </FormControl>

                                    {/* Настройки */}
                                    <Stack spacing={2}>
                                        <FormControl>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Switch
                                                    checked={formData.skip_count_in_stats}
                                                    onChange={(e) => handleChange('skip_count_in_stats', e.target.checked)}
                                                    disabled={loading}
                                                />
                                                <Box>
                                                    <Typography level="title-md">Не учитывать пропуск в статистике</Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        Пропуск этого вопроса не будет влиять на общую статистику
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </FormControl>

                                        <FormControl>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Switch
                                                    checked={formData.is_active}
                                                    onChange={(e) => handleChange('is_active', e.target.checked)}
                                                    disabled={loading}
                                                />
                                                <Box>
                                                    <Typography level="title-md">Активный вопрос</Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        {formData.is_active 
                                                            ? 'Вопрос будет доступен в тестах' 
                                                            : 'Вопрос будет скрыт от пользователей'}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </FormControl>
                                    </Stack>

                                    {/* Кнопки */}
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                            loading={loading}
                                            disabled={!formData.text.trim() || !formData.category_ids.length}
                                        >
                                            {question ? 'Сохранить изменения' : 'Создать вопрос'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </DialogContent>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}

// Компонент для отображения варианта ответа
function AnswerItem({ answer, index, questionType, onChange, onCorrectChange, onDelete, onImageUpload }) {
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* Чекбокс правильного ответа */}
                    <FormControl sx={{ minWidth: 120 }}>
                        <FormLabel>Правильный</FormLabel>
                        <Switch
                            checked={answer.is_correct}
                            onChange={(e) => onCorrectChange(index, e.target.checked)}
                            color={answer.is_correct ? 'success' : 'neutral'}
                        />
                    </FormControl>

                    {/* Текст ответа */}
                    <FormControl required sx={{ flex: 1 }}>
                        <FormLabel>Текст ответа</FormLabel>
                        <Input
                            value={answer.text}
                            onChange={(e) => onChange(index, 'text', e.target.value)}
                            placeholder="Введите текст ответа"
                        />
                    </FormControl>

                    {/* Кнопка удаления */}
                    <IconButton
                        color="danger"
                        variant="soft"
                        onClick={() => onDelete(index)}
                        sx={{ mt: 2 }}
                    >
                        <Trash size={18} />
                    </IconButton>
                </Box>

                {/* Описание ответа */}
                <FormControl>
                    <FormLabel>Описание ответа (необязательно)</FormLabel>
                    <RichTextEditor
                        value={answer.description}
                        onChange={(value) => onChange(index, 'description', value)}
                        onImageUpload={onImageUpload}
                        placeholder="Введите описание ответа"
                        sx={{ 
                            '& .ProseMirror': {
                                minHeight: '100px'
                            }
                        }}
                    />
                </FormControl>

                {/* Статус */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                        size="sm" 
                        variant="soft" 
                        color={answer.is_correct ? 'success' : 'neutral'}
                    >
                        {answer.is_correct ? 'Правильный ответ' : 'Неправильный ответ'}
                    </Chip>
                    <Chip 
                        size="sm" 
                        variant={answer.is_active ? 'soft' : 'outlined'}
                        color={answer.is_active ? 'success' : 'neutral'}
                    >
                        {answer.is_active ? 'Активен' : 'Неактивен'}
                    </Chip>
                </Box>
            </Stack>
        </Sheet>
    );
}